// src/components/MakeOfferModal.js — extracted from App.js
import React, { useState, useRef } from "react";
import { supabase } from "../lib/supabase";
import { isOfferLive, compressImage } from "../lib/utils";

const LABEL_STYLE = {fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",display:"block",marginBottom:5};

// ─── Offer constants ─────────────────────────────────────────────────────────
export const OFFER_MIN_PRICE   = 1000;
export const OFFER_PLATFORM_FEE = 500;
export const OFFER_TTL_DAYS    = 7;
export const OFFER_CONDITIONS  = ["Mint","Near Mint","Excellent","Very Good","Good","Fair","Project / Restoration"];
export const OFFER_WITHDRAW_REASONS = ["Sold elsewhere", "Condition worse than I thought", "Decided to keep it", "Buyer hasn't responded", "Other"];

// isOfferLive imported from utils
// liveOffers imported from utils
// acceptedOffers imported from utils


// ─── MakeOfferModal ───────────────────────────────────────────────────────────
export function MakeOfferModal({ request, sellerId, sellerName, onClose, onPost, toast }) {
  const [form, setForm] = useState({ price: "", condition: "", note: "" });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k,v) => setForm(p => ({...p, [k]: v}));
  const price = parseInt(form.price) || 0;
  const net = Math.max(0, price - OFFER_PLATFORM_FEE);
  const aboveBudget = request.budget && price > parseInt(request.budget);

  const addPhotos = (files) => {
    if (!files || files.length===0) return;
    const incoming = Array.from(files).slice(0, 4 - photos.length).map(f => ({ file:f, preview:URL.createObjectURL(f) }));
    setPhotos(prev => [...prev, ...incoming].slice(0, 4));
  };

  const submit = async () => {
    if (price < OFFER_MIN_PRICE) { toast(`Minimum offer is ₹${OFFER_MIN_PRICE.toLocaleString("en-IN")}.`); return; }
    if (!form.condition) { toast("Pick a condition."); return; }
    setSubmitting(true);
    const offerId = "o-" + Date.now() + Math.random().toString(36).slice(2,5);
    const photoUrls = [];
    for (let i = 0; i < photos.length; i++) {
      try {
        const compressed = await compressImage(photos[i].file, 1600, 0.88);
        const path = `offers/${request.id}/${offerId}/${i}.jpg`;
        const { error } = await supabase.storage.from("watch-photos").upload(path, compressed, { upsert:true, contentType:"image/jpeg" });
        if (!error) {
          const { data:{publicUrl} } = supabase.storage.from("watch-photos").getPublicUrl(path);
          photoUrls.push(publicUrl);
        }
      } catch {}
    }
    const now = new Date();
    const expires = new Date(now.getTime() + OFFER_TTL_DAYS*24*60*60*1000);
    const newOffer = {
      id: offerId,
      sellerId, sellerName,
      price, condition: form.condition,
      photos: photoUrls,
      note: form.note.trim(),
      status: "open",
      proposedAt: now.toISOString(),
      expiresAt: expires.toISOString(),
    };
    setSubmitting(false);
    onPost(newOffer);
  };

  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal fade-in" style={{maxWidth:520}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>
        <div style={{marginBottom:18}}>
          <div className="sl" style={{marginBottom:4}}>Make an offer</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700}}>{request.watch}</div>
          {request.budget && <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",marginTop:4}}>Buyer budget: ₹{parseInt(request.budget).toLocaleString("en-IN")}</div>}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label className="fl">Your price (₹) *</label>
            <input type="number" inputMode="numeric" min={OFFER_MIN_PRICE} value={form.price} onChange={e=>update("price", e.target.value)} placeholder={`Min ${OFFER_MIN_PRICE}`} autoComplete="off" required/>
          </div>
          <div>
            <label className="fl">Condition *</label>
            <select value={form.condition} onChange={e=>update("condition", e.target.value)}>
              <option value="">Select</option>
              {OFFER_CONDITIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {aboveBudget && (
          <div style={{background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#92400E"}}>
            ⚠ This is above the buyer's stated budget of ₹{parseInt(request.budget).toLocaleString("en-IN")}. They may decline.
          </div>
        )}

        <div style={{marginBottom:12}}>
          <label className="fl">Reference photos (up to 4)</label>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {photos.map((p,i)=>(
              <div key={i} style={{position:"relative"}}>
                <img src={p.preview} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:8,border:"1.5px solid var(--bd)"}}/>
                <button onClick={()=>setPhotos(prev=>prev.filter((_,x)=>x!==i))} style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:"#fff",border:"1px solid var(--bd)",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
              </div>
            ))}
            {photos.length < 4 && (
              <label style={{width:72,height:72,border:"1.5px dashed var(--bd)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",background:"var(--bg2)",fontSize:24,color:"var(--ink3)"}}>+
                <input type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>addPhotos(e.target.files)}/>
              </label>
            )}
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label className="fl">Notes (optional)</label>
          <textarea value={form.note} onChange={e=>update("note", e.target.value)} rows={2} placeholder="Anything the buyer should know — provenance, service history, accessories." style={{resize:"vertical"}} autoCapitalize="sentences"/>
        </div>

        <div style={{background:"var(--bg2)",borderRadius:10,padding:"12px 14px",marginBottom:14,fontSize:13}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--ink2)"}}>Buyer pays</span><span style={{fontFamily:"'Playfair Display',serif"}}>₹{price.toLocaleString("en-IN")}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--ink3)"}}>− THC platform fee</span><span style={{color:"var(--ink3)"}}>₹{OFFER_PLATFORM_FEE}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid var(--bd)",paddingTop:6,marginTop:6,fontWeight:600}}><span>You receive</span><span style={{color:"var(--grn)"}}>₹{net.toLocaleString("en-IN")}</span></div>
        </div>

        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.08em",marginBottom:10}}>OFFER EXPIRES IN {OFFER_TTL_DAYS} DAYS · YOU CAN WITHDRAW BEFORE BUYER ACCEPTS</div>

        <button className="btn-ink" style={{width:"100%",padding:"12px 0",fontSize:14,opacity:submitting?0.6:1}} onClick={submit} disabled={submitting}>
          {submitting ? "Posting offer…" : "Post offer →"}
        </button>
      </div>
    </div>
  );
}


// ─── WithdrawOfferModal ───────────────────────────────────────────────────────
export function WithdrawOfferModal({ offer, onClose, onConfirm }) {
  const [reason, setReason] = useState(OFFER_WITHDRAW_REASONS[0]);
  const [other, setOther]   = useState("");
  const final = reason === "Other" ? (other.trim() || "Other") : reason;
  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal fade-in" style={{maxWidth:420}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>
        <div style={{marginBottom:16}}>
          <div className="sl">Withdraw offer</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,marginTop:4}}>₹{offer.price.toLocaleString("en-IN")} · {offer.condition}</div>
        </div>
        <label className="fl">Reason</label>
        <select value={reason} onChange={e=>setReason(e.target.value)} style={{marginBottom:10}}>
          {OFFER_WITHDRAW_REASONS.map(r => <option key={r}>{r}</option>)}
        </select>
        {reason === "Other" && (
          <input value={other} onChange={e=>setOther(e.target.value)} placeholder="Tell the buyer briefly" style={{marginBottom:10}}/>
        )}
        <div style={{display:"flex",gap:8,marginTop:6}}>
          <button className="btn-ghost" style={{flex:1,fontSize:12}} onClick={onClose}>Cancel</button>
          <button className="btn-ink" style={{flex:1,fontSize:12,background:"var(--red)"}} onClick={()=>onConfirm(final)}>Withdraw</button>
        </div>
      </div>
    </div>
  );
}


// ─── AddressConfirmModal ──────────────────────────────────────────────────────
export function AddressConfirmModal({ request, offer, defaultAddress, onClose, onConfirm }) {
  const [addr, setAddr] = useState(defaultAddress || { line1:"", line2:"", city:"", state:"", postalCode:"", country:"India", recipientName:"", recipientPhone:"" });
  const [err, setErr] = useState("");

  const submit = (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!addr.recipientName.trim()) { setErr("Recipient name required."); return; }
    if (!addr.recipientPhone.trim()) { setErr("Contact phone required for courier delivery."); return; }
    if (!addr.line1.trim()) { setErr("Address line 1 required."); return; }
    if (!addr.city.trim()) { setErr("City required."); return; }
    if (!addr.state.trim()) { setErr("State required."); return; }
    if (!/^\d{6}$/.test(addr.postalCode)) { setErr("PIN code must be 6 digits."); return; }
    onConfirm({
      ...addr,
      recipientName: addr.recipientName.trim(),
      recipientPhone: addr.recipientPhone.trim(),
      line1: addr.line1.trim(),
      line2: addr.line2.trim(),
      city: addr.city.trim(),
      state: addr.state.trim(),
      country: (addr.country || "India").trim(),
    });
  };

  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <form className="modal fade-in" style={{maxWidth:480,maxHeight:"90vh",overflowY:"auto"}} onSubmit={submit} autoComplete="on">
        <button type="button" onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>
        <div className="sl" style={{marginBottom:4}}>Shipping address</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,marginBottom:4}}>{request.watch}</div>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.06em",marginBottom:14}}>From {offer.sellerName} · ₹{offer.price.toLocaleString("en-IN")}</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={LABEL_STYLE}>RECIPIENT NAME</label>
            <input value={addr.recipientName} onChange={e=>setAddr(p=>({...p,recipientName:e.target.value}))}
              type="text" autoComplete="name" autoCapitalize="words" required placeholder="Your name" style={{fontSize:13}}/>
          </div>
          <div>
            <label style={LABEL_STYLE}>CONTACT PHONE</label>
            <input value={addr.recipientPhone} onChange={e=>setAddr(p=>({...p,recipientPhone:e.target.value}))}
              type="tel" inputMode="tel" autoComplete="tel" pattern="[+0-9 ()\-]{7,18}" required placeholder="+91 98xxx xxxxx" style={{fontSize:13}}/>
          </div>
        </div>

        <div style={{marginBottom:10}}>
          <label style={LABEL_STYLE}>ADDRESS LINE 1</label>
          <input value={addr.line1} onChange={e=>setAddr(p=>({...p,line1:e.target.value}))}
            type="text" autoComplete="address-line1" autoCapitalize="words" required placeholder="Flat / house no., building, street" style={{fontSize:13}}/>
        </div>
        <div style={{marginBottom:10}}>
          <label style={LABEL_STYLE}>ADDRESS LINE 2 <span style={{opacity:.5}}>(optional)</span></label>
          <input value={addr.line2} onChange={e=>setAddr(p=>({...p,line2:e.target.value}))}
            type="text" autoComplete="address-line2" autoCapitalize="words" placeholder="Area, locality, landmark" style={{fontSize:13}}/>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={LABEL_STYLE}>CITY</label>
            <input value={addr.city} onChange={e=>setAddr(p=>({...p,city:e.target.value}))}
              type="text" autoComplete="address-level2" autoCapitalize="words" required placeholder="Mumbai" style={{fontSize:13}}/>
          </div>
          <div>
            <label style={LABEL_STYLE}>STATE</label>
            <input value={addr.state} onChange={e=>setAddr(p=>({...p,state:e.target.value}))}
              type="text" autoComplete="address-level1" autoCapitalize="words" required placeholder="Maharashtra" style={{fontSize:13}}/>
          </div>
          <div>
            <label style={LABEL_STYLE}>PIN CODE</label>
            <input value={addr.postalCode} onChange={e=>setAddr(p=>({...p,postalCode:e.target.value.replace(/\D/g,"").slice(0,6)}))}
              type="text" inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{6}" maxLength={6} required placeholder="400001"
              style={{fontSize:13,fontFamily:"'DM Mono',monospace"}}/>
          </div>
          <div>
            <label style={LABEL_STYLE}>COUNTRY</label>
            <input value={addr.country} onChange={e=>setAddr(p=>({...p,country:e.target.value}))}
              type="text" autoComplete="country-name" autoCapitalize="words" style={{fontSize:13}}/>
          </div>
        </div>

        {err && <div style={{color:"var(--red)",fontSize:12,marginBottom:10,background:"var(--rbg)",border:"1px solid var(--rbd)",borderRadius:6,padding:"8px 12px"}}>{err}</div>}

        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.06em",marginBottom:10}}>SHARED ONLY WITH THE ACCEPTED SELLER · UPDATE PROFILE FOR FUTURE ORDERS</div>

        <button type="submit" className="btn-ink" style={{width:"100%",padding:"12px 0",fontSize:14}}>
          Continue to payment ₹{offer.price.toLocaleString("en-IN")} →
        </button>
      </form>
    </div>
  );
}


// ─── EditOfferPriceModal ──────────────────────────────────────────────────────
export function EditOfferPriceModal({ offer, onClose, onConfirm, toast }) {
  const [newPrice, setNewPrice] = useState("");
  const v = parseInt(newPrice) || 0;
  const submit = () => {
    if (v < OFFER_MIN_PRICE) { toast(`Minimum is ₹${OFFER_MIN_PRICE.toLocaleString("en-IN")}.`); return; }
    if (v >= offer.price) { toast("New price must be lower than current."); return; }
    onConfirm(v);
  };
  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal fade-in" style={{maxWidth:380}}>
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>
        <div className="sl" style={{marginBottom:4}}>Lower your offer</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,marginBottom:14}}>Current: ₹{offer.price.toLocaleString("en-IN")}</div>
        <label className="fl">New price (₹) — must be lower</label>
        <input type="number" inputMode="numeric" min={OFFER_MIN_PRICE} max={offer.price - 1} value={newPrice} onChange={e=>setNewPrice(e.target.value)} placeholder={`Less than ${offer.price}`} style={{marginBottom:14}} autoComplete="off" required/>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:14}}>You receive ₹{Math.max(0, v - OFFER_PLATFORM_FEE).toLocaleString("en-IN")} after ₹{OFFER_PLATFORM_FEE} platform fee.</div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn-ghost" style={{flex:1,fontSize:12}} onClick={onClose}>Cancel</button>
          <button className="btn-ink" style={{flex:1,fontSize:12}} onClick={submit}>Update price</button>
        </div>
      </div>
    </div>
  );
}

